/**
 * Focus-trap helper — Story 5.4 (Critical Scope Decision #10).
 *
 * Internal helper used exclusively by `ShortcutsCheatsheet`. Lives in
 * `__lib/` per the api-handler precedent (Stories 4.3 / 4.4 use
 * `api/handlers/submitter/__lib/`); the underscore prefix signals
 * "shared internally but NOT a public component export." If a future
 * story needs the helper outside the components tree, promote then.
 *
 * Behavior:
 *   - `getFocusable(container)` returns an array of focusable descendants
 *     in document order. Selectors match the modal contract: any
 *     `[tabindex]:not([tabindex="-1"])`, enabled `<button>` / `<input>` /
 *     `<select>` / `<textarea>`, and `<a href>`.
 *   - `wrapTabFocus(event, first, last)` redirects Tab on the last element
 *     to the first, and Shift+Tab on the first element to the last. Caller
 *     is responsible for binding the listener while the modal is open.
 *
 * NOT exported via any component barrel. Imported directly by
 * `web/src/components/ShortcutsCheatsheet.tsx`.
 */

const FOCUSABLE_SELECTOR = [
  '[tabindex]:not([tabindex="-1"])',
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
].join(', ');

/**
 * Returns an ordered array of focusable elements inside `container`. Empty
 * array if no focusables (the caller may then choose to focus the
 * container itself or short-circuit).
 *
 * Filters out elements with explicit `tabindex="-1"` even on
 * `button`/`input`/`select`/`textarea`/`a[href]` matches — the comma
 * selector list would otherwise match an element via its tag selector
 * and ignore the negated-tabindex filter.
 */
export function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  return Array.from(nodes).filter(
    (el) => el.getAttribute('tabindex') !== '-1',
  );
}

/**
 * Applies Tab / Shift+Tab wrap behavior. Caller must:
 *   - register the keydown listener while the modal is open
 *   - call this with the event + the first/last focusable elements
 *   - clean up on close
 *
 * If `first === last` (single focusable), Tab keeps focus on that element.
 */
export function wrapTabFocus(
  event: KeyboardEvent,
  first: HTMLElement,
  last: HTMLElement,
): void {
  if (event.key !== 'Tab') return;
  const active = document.activeElement as HTMLElement | null;
  if (event.shiftKey) {
    if (active === first || active === null) {
      event.preventDefault();
      last.focus();
    }
  } else {
    if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
